import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal, DefinitionList, DataPair, DataTable, Notice } from '../patterns';
import {
  getXeroPreview,
  importXero,
  XeroInvoicePayload,
  XeroImportResult,
  Procurement,
} from '../../services/procurementService';

/**
 * "Import from Xero" modal — Spec B Task 8.4.
 *
 * Three states:
 *   1. loading   — modal opens, fires GET /procurement/xero/preview.
 *   2. preview   — shows invoice header (date / supplier / number / total) and
 *                  line items in a DataTable. Footer: Cancel + Confirm import.
 *   3. importing — Confirm has been clicked. Buttons disabled; importing label
 *                  swapped onto the primary button. On success the parent's
 *                  onImported handler fires (parent closes us + refreshes).
 *   4. duplicate — POST /xero/import returned 409. Shows duplicateError + a
 *                  "View existing" button that navigates to the procurement
 *                  list filtered by xeroInvoiceId, then closes via onCancel.
 *
 * The pre-import "X drafts will be created" callout from the spec is not
 * implementable without either a backend dry-run endpoint or a frontend
 * catalog probe per line item — neither exists. The parent surfaces
 * `result.draftProductsCreated.length` after onImported instead (Task 8.5).
 */
export interface XeroImportModalProps {
  open: boolean;
  onCancel: () => void;
  /** Fired on successful import. Parent should close the modal + refresh the list. */
  onImported: (result: XeroImportResult) => void;
}

type State =
  | { kind: 'loading' }
  | { kind: 'preview'; invoice: XeroInvoicePayload }
  | { kind: 'importing'; invoice: XeroInvoicePayload }
  | { kind: 'duplicate'; existing: Procurement[]; invoiceId: string };

interface LineItem {
  Description: string;
  Quantity: number;
  UnitAmount: number;
  LineAmount: number;
}

const XeroImportModal: React.FC<XeroImportModalProps> = ({ open, onCancel, onImported }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: 'loading' });

  // Fetch preview each time the modal opens. Bail on error (simple).
  useEffect(() => {
    if (!open) return;
    setState({ kind: 'loading' });
    let cancelled = false;
    getXeroPreview()
      .then((invoice) => {
        if (!cancelled) setState({ kind: 'preview', invoice });
      })
      .catch(() => {
        if (!cancelled) onCancel();
      });
    return () => {
      cancelled = true;
    };
  }, [open, onCancel]);

  const handleConfirm = async () => {
    if (state.kind !== 'preview') return;
    const invoice = state.invoice;
    setState({ kind: 'importing', invoice });
    try {
      const result = await importXero(invoice);
      onImported(result);
    } catch (err: unknown) {
      const errObj = err as { response?: { status?: number; data?: { existing?: Procurement[] } } };
      if (errObj?.response?.status === 409) {
        setState({
          kind: 'duplicate',
          existing: errObj.response.data?.existing ?? [],
          invoiceId: invoice.InvoiceID,
        });
      } else {
        // Unknown error: drop back to preview so the user can retry.
        setState({ kind: 'preview', invoice });
      }
    }
  };

  const handleViewExisting = () => {
    if (state.kind !== 'duplicate') return;
    navigate(`/consumables/procurement?xeroInvoiceId=${encodeURIComponent(state.invoiceId)}`);
    onCancel();
  };

  const lineItemColumns = [
    {
      key: 'description',
      label: t('consumables.procurement.list.columns.product', { defaultValue: 'Description' }),
      render: (l: LineItem) => l.Description,
    },
    {
      key: 'quantity',
      label: t('consumables.procurement.list.columns.quantity', { defaultValue: 'Quantity' }),
      align: 'right' as const,
      render: (l: LineItem) => String(l.Quantity),
    },
    {
      key: 'unitPrice',
      label: t('consumables.procurement.list.columns.unitPrice', { defaultValue: 'Unit price' }),
      align: 'right' as const,
      render: (l: LineItem) => l.UnitAmount.toFixed(2),
    },
    {
      key: 'lineTotal',
      label: t('consumables.procurement.list.columns.totalPrice', { defaultValue: 'Line total' }),
      align: 'right' as const,
      render: (l: LineItem) => l.LineAmount.toFixed(2),
    },
  ];

  return (
    <Modal
      show={open}
      onHide={onCancel}
      size="lg"
      title={t('consumables.procurement.xero.modal.title')}
    >
      {state.kind === 'loading' && (
        <Modal.Body>
          <div role="status">{t('consumables.procurement.xero.modal.loading')}</div>
        </Modal.Body>
      )}

      {(state.kind === 'preview' || state.kind === 'importing') && (
        <>
          <Modal.Body>
            <DefinitionList columns={2}>
              <DataPair
                label={t('consumables.procurement.list.columns.referenceNumber', { defaultValue: 'Invoice #' })}
                value={state.invoice.InvoiceNumber}
              />
              <DataPair
                label={t('consumables.procurement.list.columns.date', { defaultValue: 'Date' })}
                value={state.invoice.Date}
              />
              <DataPair
                label={t('consumables.procurement.list.columns.supplier', { defaultValue: 'Supplier' })}
                value={state.invoice.Contact?.Name ?? ''}
              />
              <DataPair
                label={t('consumables.procurement.list.columns.totalPrice', { defaultValue: 'Total' })}
                value={state.invoice.Total.toFixed(2)}
              />
            </DefinitionList>

            <div style={{ marginTop: 16 }}>
              <DataTable<LineItem>
                rows={state.invoice.LineItems}
                rowKey={(_l) => `${_l.Description}-${_l.Quantity}-${_l.UnitAmount}-${_l.LineAmount}`}
                columns={lineItemColumns}
              />
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={onCancel}
              disabled={state.kind === 'importing'}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={handleConfirm}
              disabled={state.kind === 'importing'}
            >
              {state.kind === 'importing'
                ? t('consumables.procurement.xero.modal.importing')
                : t('consumables.procurement.xero.modal.confirm')}
            </button>
          </Modal.Footer>
        </>
      )}

      {state.kind === 'duplicate' && (
        <>
          <Modal.Body>
            <Notice tone="danger">
              {t('consumables.procurement.xero.modal.duplicateError')}
            </Notice>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={onCancel}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={handleViewExisting}
            >
              {t('consumables.procurement.xero.modal.viewExisting')}
            </button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default XeroImportModal;
